import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import { getJobs } from 'api/jobs'
import { useRootLoaderData } from 'components/Root.loader'
import { Job } from 'entities/Job'
import { useConfirm, useAlert } from 'hooks/useDialog'
import useEditDocuments from 'hooks/useEditDocuments'
import { ActionKind, useReducerContext } from 'hooks/useReducerContext'
import { useSnackbar } from 'notistack'
import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import JobsList from './JobsList'
import RightBottomPaper from './RightBottomPaper'
import TitleBar from './TitleBar'

const MSG_UPLOAD_DONE = '登録処理が完了いたしました'
const TICK_INTERVAL = 2 * 1000
const INVALID_RETRY_DURATION = 30 * 1000

// if fromDate changes it will start again polling jobs
const JobsBox = (props: { fromDate: Date }) => {
  const [minimized, setMinimized] = useState(false)
  const [fromDate] = useState(props.fromDate) // never update fromdate
  const rootData = useRootLoaderData()
  const vGroups = rootData.verifyGroups
  const [jobs, setJobs] = useState<Job[]>([])
  const [_, dispatch] = useReducerContext()
  const confirm = useConfirm()
  const editDocuments = useEditDocuments()
  const location = useLocation()
  const isHome = location.pathname.endsWith('/home')
  const navigate = useNavigate()
  const alert = useAlert()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const timeoutId = useRef<NodeJS.Timeout | undefined>(undefined)
  const fromDateRef = useRef<Date | null>(null)

  const invalidRetryStartTimeRef = useRef<number | null>(null)
  const [isInvalidWaiting, setIsInvalidWaiting] = useState(false)

  const justOpened =
    !fromDateRef.current || fromDateRef.current.getTime() !== props.fromDate.getTime()
  const processing = jobs.some((j) => j.isProcessing()) || isInvalidWaiting
  const someFailed = jobs.some((j) => j.isFailed())
  const succeeded = !(processing || someFailed) && jobs.length > 0
  const isAllJobsOneType = jobs.every((x) => x.docType === jobs[0].docType)
  const isAllJobsNormal = jobs.every((x) => x.isVerify === false)
  const showEdit = succeeded && isAllJobsOneType && isAllJobsNormal

  const clearInvalidStatus = useCallback(() => {
    if (invalidRetryStartTimeRef.current) {
      invalidRetryStartTimeRef.current = null
      setIsInvalidWaiting(false)
    }
  }, [])

  const clearTimers = useCallback(() => {
    if (timeoutId.current) {
      clearInvalidStatus()
      clearTimeout(timeoutId.current)
    }
    timeoutId.current = undefined
  }, [timeoutId, clearInvalidStatus])

  const showNotification = useCallback(
    (jobs: Job[]) => {
      const action = () => {
        return (
          <>
            <Button
              variant="contained"
              sx={{
                color: 'grey.800',
                backgroundColor: 'white',
                '&:hover': {
                  backgroundColor: 'grey.200',
                },
              }}
              onClick={() => {
                closeSnackbar()
                if (!timeoutId.current) dispatch({ type: ActionKind.JOBSBOX_CLOSE })
                navigate(
                  jobs[0].isVerify // the newest is 0
                    ? '/verify/home'
                    : '/normal/home',
                )
              }}
            >
              一覧へ
            </Button>
            <Link
              component="button"
              style={{ fontSize: '1.3rem', marginLeft: '10px', color: 'inherit' }}
              onClick={() => closeSnackbar()}
            >
              {' X '}
            </Link>
          </>
        )
      }
      enqueueSnackbar(MSG_UPLOAD_DONE, { action })
      clearTimers()
    },
    [clearTimers, closeSnackbar, dispatch, enqueueSnackbar, navigate],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scheduleNextTick = () => {
    timeoutId.current = setTimeout(tick, TICK_INTERVAL)
  }

  const checkStatus = useCallback(
    (jobs: Job[]): { hasInprogress: boolean; isLatestInvalid: boolean } => {
      const hasInprogress = jobs.some((j) => j.isProcessing())
      const isLatestInvalid = jobs[0].isInvalid()
      return { hasInprogress, isLatestInvalid }
    },
    [],
  )

  const handleInvalidState = useCallback(
    (jobs: Job[]) => {
      // https://git-dev.linecorp.com/LINEBRAIN/ocr-reader-project-management/issues/605
      const currentTime = Date.now()
      if (!invalidRetryStartTimeRef.current) {
        invalidRetryStartTimeRef.current = currentTime
        setIsInvalidWaiting(true)
        scheduleNextTick()
      } else {
        const elapsedTime = currentTime - invalidRetryStartTimeRef.current
        if (elapsedTime > INVALID_RETRY_DURATION) {
          showNotification(jobs)
        } else {
          scheduleNextTick()
        }
      }
    },
    [scheduleNextTick, showNotification],
  )

  const tick = useCallback(async () => {
    const fetchedJobs = await getJobs({ fromDate: fromDate, perPage: 200 })
    if (!fetchedJobs) throw new Error('jobs not found') // timing issue?
    fetchedJobs.forEach((j) => {
      j.decideIsVerify(vGroups)
    }) // refactor
    const originalIds = new Set(fetchedJobs.map((j) => j.originalJobID))

    // filter out parent documents
    // count of jobs doubles in max
    const filteredJobs = fetchedJobs.filter((j) => !originalIds.has(j.jobID)).slice(0, 100)
    setJobs(filteredJobs)
    const { hasInprogress, isLatestInvalid } = checkStatus(filteredJobs)
    try {
      // still processing or just opened(opened is a closure val, openedBefore is a ref val)
      if (hasInprogress || fromDateRef.current!.getTime() !== props.fromDate.getTime()) {
        scheduleNextTick()
        clearInvalidStatus()
      } else if (isLatestInvalid) {
        handleInvalidState(filteredJobs)
      } else {
        showNotification(filteredJobs)
      }
    } catch (_e) {
      alert({ message: 'job 取得に失敗しました。' })
    }
  }, [
    fromDate,
    checkStatus,
    vGroups,
    props.fromDate,
    scheduleNextTick,
    clearInvalidStatus,
    handleInvalidState,
    showNotification,
    alert,
  ])

  useEffect(() => {
    if (fromDateRef.current?.getTime() === props.fromDate.getTime()) return
    fromDateRef.current = props.fromDate
    if (timeoutId.current) {
      // reschedule with new tick func
      clearTimers()
    }
    scheduleNextTick()
    // don't clear timeoutId here please
  }, [fromDate, tick, props.fromDate, scheduleNextTick, clearTimers])

  useEffect(() => {
    if (!processing) {
      return
    }

    const askBeforeLeaving = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', askBeforeLeaving)

    return () => {
      window.removeEventListener('beforeunload', askBeforeLeaving)
    }
  }, [processing])

  const handleClose = () => {
    if (processing) {
      confirm({
        title: '処理ステータスの表示を閉じますか？',
        message: [
          '登録処理を実行中です。登録が完了した文書は更新ボタンで一覧を更新することで確認可能となります。',
        ],
        onConfirm() {
          if (timeoutId.current) {
            clearTimers()
          }
          dispatch({ type: ActionKind.JOBSBOX_CLOSE })
        },
        onCancel() {
          /* do nothing */
        },
        headerAlign: 'center',
      })
    } else {
      if (timeoutId.current) {
        clearTimers()
      }
      dispatch({ type: ActionKind.JOBSBOX_CLOSE })
    }
  }

  const handleMiniClick = () => {
    setMinimized((min) => !min)
  }

  const handleEditClick = () => {
    const objectIds = jobs
      .filter((j) => j.fileExt !== 'zip')
      .map((x) => x.objectID)
      .filter(Boolean)

    if (objectIds.length === 0) return

    // isVerifyは一緒の前提
    editDocuments(jobs[0].isVerify ? 'verify' : 'normal', objectIds, isHome)
    dispatch({ type: ActionKind.JOBSBOX_CLOSE })
  }

  return (
    <RightBottomPaper variant="outlined" square>
      <TitleBar
        processing={processing || justOpened}
        someFailed={someFailed}
        minimized={minimized}
        showEdit={showEdit}
        onCloseClick={handleClose}
        onMinClick={handleMiniClick}
        onEditClick={handleEditClick}
      />
      {!minimized && <JobsList jobs={jobs} someFailed={someFailed} />}
    </RightBottomPaper>
  )
}

export default JobsBox
